import { getEnumFromValue } from '@NGUtils/functions';
import { formatFetchedInventoryItems, formatFetchedWornItems } from '@character/store/characterStore.functions';
import { axiosClient } from '@services/axios';
import qs from 'qs';
import { IItem } from 'src/features/character/features/characterInventory/components/Avatar/Avatar.type';
import { EAvatarItemType, EAvatarType } from 'src/styles/theme/utils/enums';
import {
	IBuyChestResponse,
	IEquipItemsResponse,
	IForgeItemPostResponse,
	IForgeItemResponse,
	IOpenChestResponse,
	IResetWornItemsResponse,
	IUnequipItemsResponse,
	IUserInventoryResponse,
	IWornItemsResponse,
	TUserInventoryGetResponse,
	TWornItemsGetResponse
} from './inventory.services.type';

const postForgeItem = async (quantity: number, id?: IItem['id']): Promise<IForgeItemPostResponse> => {
	if (!id) {
		throw new Error('id is not defined');
	}

	const res = (await axiosClient.post('/inventory/items/reforge', {
		itemId: id,
		quantity: quantity
	})) as IForgeItemResponse;

	return {
		items: res.data.data.items,
		quantity
	};
};

const postOpenChest = async (id: number, quantity: number) => {
	const res = (await axiosClient.post('/inventory/chest/open', {
		chestId: id,
		quantity: quantity
	})) as IOpenChestResponse;

	return {
		items: res.data.data.items,
		quantity
	};
};

const postBuyChest = async (chestId: number, quantity: number) => {
	const res: IBuyChestResponse = await axiosClient.post('/inventory/chest/buy', {
		chestId,
		quantity: quantity
	});

	return res.data.data;
};

const populateAssetSrc = {
	fields: ['id', 'url', 'alternativeText', 'formats']
};

const getInventoryItems = async (avatarType?: EAvatarType): Promise<TUserInventoryGetResponse> => {
	const inventoryItemsPayload: {
		fields: string[];
		populate: {
			srcPreviewM?: typeof populateAssetSrc;
			srcPreviewF?: typeof populateAssetSrc;
			srcPreviewMBack?: typeof populateAssetSrc;
			srcPreviewFBack?: typeof populateAssetSrc;
			srcItemM?: typeof populateAssetSrc;
			srcItemF?: typeof populateAssetSrc;
			srcItemMBack?: typeof populateAssetSrc;
			srcItemFBack?: typeof populateAssetSrc;
			srcPreviewMCropped?: typeof populateAssetSrc;
			srcPreviewFCropped?: typeof populateAssetSrc;
		};
	} = {
		fields: ['id', 'type', 'rarity', 'label', 'zIndex', 'zIndex2', 'description', 'category', 'color'],
		populate: {
			srcPreviewM: populateAssetSrc,
			srcPreviewMBack: populateAssetSrc,
			srcItemM: populateAssetSrc,
			srcItemMBack: populateAssetSrc,
			srcPreviewMCropped: populateAssetSrc,
			// the female assets are fetched by default because some of them don't have assets so we can fallback to male assets if needed
			srcPreviewFBack: populateAssetSrc,
			srcItemF: populateAssetSrc,
			srcPreviewF: populateAssetSrc,
			srcItemFBack: populateAssetSrc,
			srcPreviewFCropped: populateAssetSrc
		}
	};
	/*
// the female assets are fetched by default because some of them don't have assets so we can fallback to male assets if needed
	if (avatarType && avatarType === EAvatarType.female) {
		inventoryItemsPayload.populate = {
			srcPreviewFBack: populateAssetSrc,
			srcItemF: populateAssetSrc,
			srcPreviewF: populateAssetSrc,
			srcItemFBack: populateAssetSrc,
			srcPreviewFCropped: populateAssetSrc
		};
	}
 */
	const inventoryItemsQuery = qs.stringify(inventoryItemsPayload, {
		encodeValuesOnly: true
	});

	const res = (await axiosClient.get(`/inventories?type=user&${inventoryItemsQuery}`)) as IUserInventoryResponse;

	return res.data.data.items;
};

const getAndFormatInventoryItems = async (avatarType?: EAvatarType): Promise<IItem[]> => {
	const inventoryItemsData = await getInventoryItems(avatarType);
	const formattedInventoryItems = formatFetchedInventoryItems(
		inventoryItemsData,
		getEnumFromValue(avatarType, EAvatarType)
	);

	return formattedInventoryItems;
};

export const wornItemsPayload = {
	fields: ['id', 'type', 'rarity', 'zIndex', 'zIndex2', 'isReference', 'label', 'description', 'category', 'color'],
	populate: {
		srcPreviewM: populateAssetSrc,
		srcPreviewF: populateAssetSrc,
		srcPreviewMBack: populateAssetSrc,
		srcItemM: populateAssetSrc,
		srcItemMBack: populateAssetSrc,
		srcPreviewFBack: populateAssetSrc,
		srcItemF: populateAssetSrc,
		srcItemFBack: populateAssetSrc,
		srcPreviewMCropped: populateAssetSrc,
		srcPreviewFCropped: populateAssetSrc
	}
};

const getWornItems = async (): Promise<TWornItemsGetResponse> => {
	const wornItemsQuery = qs.stringify(wornItemsPayload, {
		encodeValuesOnly: true
	});
	const res = (await axiosClient.get(`inventories/worn-items?${wornItemsQuery}`)) as IWornItemsResponse;

	return res.data.data.wornItems;
};

const getAndFormatWornItems = async (avatarType?: EAvatarType): Promise<IItem[]> => {
	if (!avatarType) {
		throw new Error('avatarType is not defined');
	}

	const wornItemsData = await getWornItems();
	const formattedWornItems = formatFetchedWornItems(wornItemsData, avatarType);

	return formattedWornItems;
};

const postEquipItems = async (ids: number[]) => {
	const res = (await axiosClient.post(`inventory/equip-items`, { itemsId: ids })) as IEquipItemsResponse;
	return res;
};

const postUnequipItems = async (types: EAvatarItemType[]) => {
	const res = (await axiosClient.post(`inventory/unequip-items`, { itemTypes: types })) as IUnequipItemsResponse;
	return res;
};

async function resetReferenceItems() {
	const res = (await axiosClient.post(
		`inventory/items/reset-reference-items`,
		wornItemsPayload
	)) as IResetWornItemsResponse;

	if (!res.data?.data) {
		throw new Error('No data');
	}

	return res.data.data;
}

export {
	postForgeItem,
	postOpenChest,
	postBuyChest,
	getInventoryItems,
	getAndFormatInventoryItems,
	getWornItems,
	getAndFormatWornItems,
	postEquipItems,
	postUnequipItems,
	resetReferenceItems
};
